import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { rateService } from "../services/rateService";
import { notify } from "../assets/helpers/toast";

/**
 * Da formato a un número en estilo venezolano: 13425 -> "13.425,00".
 *
 * @param {number} value Valor numérico a formatear.
 * @returns {string} Cadena con separador de miles "." y decimal ",".
 */
export const formatBs = (value) =>
  new Intl.NumberFormat("es-VE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);

/**
 * Calcula la fecha de hoy en formato ISO AAAA-MM-DD
 * (compatible con `<input type="date">`).
 *
 * @returns {string} Fecha local (ej. "2026-06-09").
 */
export const getTodayISO = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/**
 * Convierte el texto del input (acepta "," o ".") a número.
 *
 * @param {string} value Monto en USD como texto.
 * @returns {number} Número parseado (0 si no es válido).
 */
const parseUsd = (value) => {
  const parsed = parseFloat(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Lógica de negocio aislada de la Calculadora BCV (USD -> Bs.).
 * No contiene JSX; solo estado, derivados y handlers.
 */
export default function useCurrencyConverter() {
  const [rate, setRate] = useState(0);
  const [usdAmount, setUsdAmount] = useState("150.00");
  const [paymentDate, setPaymentDate] = useState(getTodayISO);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoadingRate, setIsLoadingRate] = useState(false);
  const [copied, setCopied] = useState(false);

  const copiedTimeoutRef = useRef(null);

  const bsAmount = useMemo(() => parseUsd(usdAmount) * rate, [usdAmount, rate]);
  const bsAmountFormatted = useMemo(() => formatBs(bsAmount), [bsAmount]);
  const rateFormatted = useMemo(() => rate.toFixed(4), [rate]);

  /**
   * Obtiene la tasa BCV correspondiente a una fecha.
   * - Hoy (o vacía): tasa del día (servicio principal con respaldo).
   * - Anterior: tasa histórica por fecha.
   * Si no hay tasa para la fecha, notifica y deja la tasa en 0.
   *
   * @param {string} date Fecha en formato ISO AAAA-MM-DD.
   */
  const fetchRate = useCallback(async (date) => {
    const isToday = !date || date === getTodayISO();
    setIsLoadingRate(true);
    try {
      if (isToday) {
        // Servicio PRINCIPAL: tasa del día.
        const primary = await rateService.getDollarRate();
        const primaryRate = primary?.data?.rate;
        if (primary?.status === 200 && primaryRate) {
          setRate(Number(primaryRate));
          return;
        }

        // Respaldo: último registro de tasa.
        const fallback = await rateService.getCurrencyRates({
          currency: "USD",
          date: "",
          limit: 1,
        });
        const fallbackRate = fallback?.data?.result?.[0]?.rate;
        if (fallback?.status === 200 && fallbackRate) {
          setRate(Number(fallbackRate));
          return;
        }

        notify("No se pudo obtener la tasa BCV", "error");
        setRate(0);
        return;
      }

      // Fecha anterior: tasa histórica.
      const historical = await rateService.getCurrencyRates({
        currency: "USD",
        date,
        limit: 1,
      });
      const historicalRate = historical?.data?.result?.[0]?.rate;
      if (historical?.status === 200 && historicalRate) {
        setRate(Number(historicalRate));
        return;
      }

      notify("No se encontró tasa BCV para la fecha seleccionada", "error");
      setRate(0);
    } catch (error) {
      notify("No se pudo obtener la tasa BCV", "error");
      setRate(0);
    } finally {
      setIsLoadingRate(false);
    }
  }, []);

  // Limpia el timeout del feedback "copiado" al desmontar.
  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  const handleUsdAmountChange = useCallback((event) => {
    const raw = event?.target?.value ?? "";
    // Permite solo dígitos y un separador decimal (coma o punto).
    setUsdAmount(raw.replace(/[^\d.,]/g, ""));
  }, []);

  const handlePaymentDateChange = useCallback(
    (event) => {
      const value = event?.target?.value ?? "";
      setPaymentDate(value);
      fetchRate(value);
    },
    [fetchRate]
  );

  const openModal = useCallback(() => {
    // Al abrir, siempre se reinicia a la fecha de hoy y se refresca la tasa
    // para garantizar que se muestre la tasa actualizada del día.
    const today = getTodayISO();
    setPaymentDate(today);
    setIsOpen(true);
    fetchRate(today);
  }, [fetchRate]);

  const closeModal = useCallback(() => {
    // Al cerrar se reinician los valores de búsqueda (fecha del pago).
    setIsOpen(false);
    setPaymentDate(getTodayISO());
  }, []);

  const toggleModal = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev) {
        const today = getTodayISO();
        setPaymentDate(today);
        fetchRate(today);
        return true;
      }
      setPaymentDate(getTodayISO());
      return false;
    });
  }, [fetchRate]);

  const handleCopyToClipboard = useCallback(async () => {
    const text = formatBs(bsAmount); // Sin prefijo "Bs.".

    const markCopied = () => {
      setCopied(true);
      if (copiedTimeoutRef.current) {
        clearTimeout(copiedTimeoutRef.current);
      }
      copiedTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
    };

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        markCopied();
        return;
      }

      // Fallback para navegadores sin Clipboard API.
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);

      if (ok) {
        markCopied();
      } else {
        notify("No se pudo copiar el monto", "error");
      }
    } catch (error) {
      notify("No se pudo copiar el monto", "error");
    }
  }, [bsAmount]);

  return {
    // Estado / derivados.
    rate,
    usdAmount,
    bsAmount,
    bsAmountFormatted,
    rateFormatted,
    paymentDate,
    isOpen,
    isLoadingRate,
    copied,
    // Handlers.
    fetchRate,
    handleUsdAmountChange,
    handlePaymentDateChange,
    openModal,
    closeModal,
    toggleModal,
    handleCopyToClipboard,
  };
}
